import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send } from "lucide-react";
import { useSendMessage } from './hooks/useSendMessage';
import { useTranslations } from "@/hooks/useTranslations";

type MessageInputProps = {
  channelId?: string;
  recipientId?: string;
};

export const MessageInput = ({ channelId, recipientId }: MessageInputProps) => {
  const [message, setMessage] = useState('');
  const { sendMessage, sending } = useSendMessage(channelId, recipientId);
  const { t } = useTranslations('messages');
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    
    await sendMessage(message);
    setMessage('');
  };
  
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as unknown as React.FormEvent);
    }
  };
  
  const isDisabled = sending || !message.trim() || (!channelId && !recipientId);
  const isInputDisabled = !channelId && !recipientId;
  
  return (
    <form onSubmit={handleSubmit} className="p-3 border-t border-border bg-card">
      <div className="flex items-end gap-2">
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={isInputDisabled ? t('selectConversation') : t('typeMessage')}
          className="flex-1 min-h-[40px] max-h-[120px] resize-none text-sm"
          disabled={isInputDisabled}
          rows={1}
        />
        <Button 
          type="submit" 
          disabled={isDisabled}
          size="icon"
          className="h-10 w-10 flex-shrink-0"
        >
          <Send className="h-4 w-4" />
          <span className="sr-only">{t('sendMessage')}</span>
        </Button>
      </div>
    </form>
  );
};
